import React from "react";
import { EmptyState, Icon } from "./registry";
/* ============================================================
   Fence Works — Email Sequences (editable templates)
   Premade review / referral / follow-up sequences with merge
   tags and a Google review link, sendable from work orders.
   ============================================================ */
const TRIGGERS = {
  job_completed: { label: "When a job is completed", icon: "check", color: "var(--green)" },
  quote_sent: { label: "When a quote is sent", icon: "send", color: "var(--cyan)" },
};
const MERGE_TAGS = ["{{customer}}", "{{company}}", "{{review_link}}", "{{address}}", "{{reward}}"];

function defaultSeqs() { return JSON.parse(JSON.stringify(window.VFP.DEFAULT_SETTINGS.sequences)); }

/* fill merge tags for preview / sending */
function renderTemplate(text, ctx) {
  return (text || "")
    .replaceAll("{{customer}}", ctx.customer || "there")
    .replaceAll("{{company}}", ctx.company || "our team")
    .replaceAll("{{review_link}}", ctx.reviewLink || "[add your Google review link in Settings]")
    .replaceAll("{{address}}", ctx.address || "your property")
    .replaceAll("{{reward}}", ctx.reward || "$100 Visa gift card");
}
window.renderTemplate = renderTemplate;

function EmailSequences({ store, go, params }: any) {
  const s = Object.assign({}, window.VFP.DEFAULT_SETTINGS, store.settings);
  const [seqs, setSeqs] = React.useState<any>(() => (s.sequences && s.sequences.length) ? JSON.parse(JSON.stringify(s.sequences)) : defaultSeqs());
  // deep link from a work order / customer profile: open (and scroll to) that sequence
  React.useEffect(() => {
    const want = params && params.seq;
    if (!want) return;
    setOpenSeq(want);
    setTimeout(() => { try { const el = document.querySelector(`[data-seq="${want}"]`); if (el) el.scrollIntoView({ block: "start", behavior: "smooth" }); } catch (e) {} }, 60);
  }, [params && params.seq]);
  // the review link is owned by Settings → Customer-facing; shown read-only here
  const reviewUrl = s.googleReviewUrl || "";
  const [saved, setSaved] = React.useState<any>(false);
  const [openSeq, setOpenSeq] = React.useState<any>(seqs[0] && seqs[0].id);

  function save() { store.setSettings({ sequences: seqs }); setSaved(true); setTimeout(() => setSaved(false), 1800); }
  // what's actually persisted — the on/off toggle and delete write straight
  // through to it (a paused sequence must stay paused even if the copy edits
  // below are never saved); copy edits stay local until Save.
  const stored = () => (s.sequences && s.sequences.length) ? s.sequences : defaultSeqs();
  const dirty = JSON.stringify(seqs) !== JSON.stringify(stored());
  function persistNow(fn) { setSeqs(ss => fn(ss)); store.setSettings({ sequences: fn(stored()) }); }
  const upd = (id, patch) => setSeqs(ss => ss.map(q => q.id === id ? { ...q, ...patch } : q));
  const toggleOn = (id, on) => persistNow(ss => ss.map(q => q.id === id ? { ...q, on } : q));
  const updStep = (id, i, patch) => setSeqs(ss => ss.map(q => q.id === id ? { ...q, steps: q.steps.map((st, k) => k === i ? { ...st, ...patch } : st) } : q));
  const addStep = (id) => setSeqs(ss => ss.map(q => q.id === id ? { ...q, steps: [...q.steps, { delayDays: (q.steps[q.steps.length - 1]?.delayDays || 0) + 3, subject: "", body: "" }] } : q));
  const delStep = (id, i) => setSeqs(ss => ss.map(q => q.id === id ? { ...q, steps: q.steps.filter((_, k) => k !== i) } : q));
  function addSeq() { const id = "seq-" + Date.now(); setSeqs(ss => [...ss, { id, name: "New Sequence", trigger: "job_completed", on: true, steps: [{ delayDays: 0, subject: "", body: "" }] }]); setOpenSeq(id); }
  function delSeq(id) { persistNow(ss => ss.filter(q => q.id !== id)); }

  const ctx = { customer: "Sam Carter", company: (store.settings && store.settings.companyName) || (store.company && store.company.name) || "Your Fence Co.", reviewLink: reviewUrl || "https://g.page/r/your-review-link", address: "123 Main St", reward: "$100 Visa gift card" };

  return (
    <div className="page fade-up" style={{ maxWidth: 1100 }}>
      <div className="row between" style={{ marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <button className="btn btn-ghost btn-sm" style={{ marginBottom: 10 }} onClick={() => go("settings")}><Icon name="chevright" size={13} style={{ transform: "rotate(180deg)" }} />Settings</button>
          <h1 className="display" style={{ fontSize: 28, margin: 0 }}>Email Sequences</h1>
          <p className="muted" style={{ margin: "4px 0 0" }}>Automated follow-ups for reviews, referrals &amp; quotes. Edit the copy, set the timing, and fire them from each job.</p>
        </div>
        <div className="row gap-8" style={{ flexWrap: "wrap" }}>
          {dirty && !saved && <span className="chip" style={{ color: "var(--amber)", borderColor: "var(--amber)", fontSize: 11 }}>Unsaved changes</span>}
          <button className="btn btn-ghost" onClick={addSeq}><Icon name="plus" size={15} />New Sequence</button>
          <button className="btn btn-primary" onClick={save}>{saved ? <><Icon name="check" size={15} />Saved</> : "Save sequences"}</button></div>
      </div>

      {/* review link */}
      <div className="card pad" style={{ marginBottom: 16 }}>
        <div className="row between" style={{ marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
          <span className="row gap-8 kicker kicker-teal"><Icon name="check" size={14} />Google Review Link</span>
          <button className="btn btn-ghost btn-sm" onClick={() => go("settings", { section: "social" })}><Icon name="edit" size={13} />Edit in Settings → Customer-facing</button>
        </div>
        {reviewUrl
          ? <div className="mono" style={{ fontSize: 12.5, padding: "9px 12px", background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: 10, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{reviewUrl}</div>
          : <div className="muted" style={{ fontSize: 13, padding: "9px 12px", background: "var(--surface-2)", border: "1px dashed var(--line-2)", borderRadius: 10 }}>No review link yet — add it in Settings so the <code className="merge">{"{{review_link}}"}</code> tag has somewhere to point.</div>}
        <p className="muted" style={{ fontSize: 12.5, margin: "8px 0 0" }}>Pasted into emails wherever you use the <code className="merge">{"{{review_link}}"}</code> tag. Find it in your Google Business Profile → "Ask for reviews".</p>
      </div>

      <div style={{ display: "grid", gap: 14 }}>
        {seqs.length === 0 && (
          <div className="card pad"><EmptyState icon="mail" title="No sequences yet" sub="Add a sequence to auto-send review, referral & follow-up emails when a job finishes or a quote goes out." /></div>
        )}
        {seqs.map(seq => {
          const trig = TRIGGERS[seq.trigger] || TRIGGERS.job_completed;
          const isOpen = openSeq === seq.id;
          return (
            <div key={seq.id} data-seq={seq.id} className="card" style={{ overflow: "hidden", borderColor: isOpen ? "var(--teal-tint-2)" : "var(--line)" }}>
              <div className="seq-head" onClick={() => setOpenSeq(isOpen ? null : seq.id)}>
                <button type="button" className="seq-toggle" role="switch" aria-checked={!!seq.on}
                  aria-label={(seq.on ? "Pause" : "Activate") + " the " + (seq.name || "sequence") + " sequence"}
                  onClick={(e) => { e.stopPropagation(); toggleOn(seq.id, !seq.on); }}
                  title={seq.on ? "Active — tap to pause (saves right away)" : "Paused — tap to activate (saves right away)"}>
                  <span className={"sw" + (seq.on ? " on" : "")}><span className="knob" /></span>
                </button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <input className="seq-name" value={seq.name} onClick={e => e.stopPropagation()} onChange={e => upd(seq.id, { name: e.target.value })} />
                  <div className="row gap-8" style={{ fontSize: 12, flexWrap: "wrap" }}>
                    <span className="chip" style={{ fontSize: 11, color: seq.on ? "var(--teal)" : "var(--fg-3)", borderColor: seq.on ? "var(--teal-tint-2)" : "var(--line-2)" }}>{seq.on ? "Active" : "Paused"}</span>
                    <span className="chip" style={{ color: trig.color, borderColor: "var(--line-2)", fontSize: 11 }}><Icon name={trig.icon} size={11} />{trig.label}</span>
                    <span className="muted">{seq.steps.length} email{seq.steps.length !== 1 ? "s" : ""}</span>
                  </div>
                </div>
                <Icon name={isOpen ? "chevdown" : "chevright"} size={16} className="muted" />
              </div>
              {isOpen && (
                <div className="seq-body">
                  <div className="row gap-12" style={{ flexWrap: "wrap", marginBottom: 6 }}>
                    <div><label className="field-label">Trigger</label>
                      <select className="select" value={seq.trigger} onChange={e => upd(seq.id, { trigger: e.target.value })} style={{ width: 220 }}>
                        {Object.entries(TRIGGERS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                      </select></div>
                    <div style={{ flex: 1 }} />
                    <button className="btn btn-ghost btn-sm" style={{ alignSelf: "flex-end", color: "var(--red)" }} onClick={() => delSeq(seq.id)}><Icon name="trash" size={13} />Delete sequence</button>
                  </div>
                  {seq.steps.map((st, i) => (
                    <div key={i} className="seq-step">
                      <div className="seq-step-rail"><span className="seq-step-dot">{i + 1}</span>{i < seq.steps.length - 1 && <span className="seq-step-line" />}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="row between" style={{ marginBottom: 8 }}>
                          <div className="seq-delay">{st.delayDays === 0 ? "Send immediately" : <>Wait <input className="tnum" type="number" min="0" value={st.delayDays} onChange={e => updStep(seq.id, i, { delayDays: +e.target.value || 0 })} /> day{st.delayDays !== 1 ? "s" : ""}</>}</div>
                          {seq.steps.length > 1 && <button className="seq-del-step" onClick={() => delStep(seq.id, i)}><Icon name="x" size={13} /></button>}
                        </div>
                        <input className="input" style={{ marginBottom: 8 }} value={st.subject} onChange={e => updStep(seq.id, i, { subject: e.target.value })} placeholder="Subject line" />
                        <textarea className="textarea" style={{ minHeight: 110 }} value={st.body} onChange={e => updStep(seq.id, i, { body: e.target.value })} placeholder="Email body — use merge tags below" />
                        <details className="seq-preview">
                          <summary>Preview with sample data</summary>
                          <div className="seq-preview-box"><div style={{ fontWeight: 700, marginBottom: 6 }}>{renderTemplate(st.subject, ctx)}</div><div style={{ whiteSpace: "pre-wrap", color: "var(--fg-2)", fontSize: 13 }}>{renderTemplate(st.body, ctx)}</div></div>
                        </details>
                      </div>
                    </div>
                  ))}
                  <div className="row between" style={{ marginTop: 4, flexWrap: "wrap", gap: 8 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => addStep(seq.id)}><Icon name="plus" size={13} />Add email</button>
                    <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                      <span className="muted" style={{ fontSize: 11.5, alignSelf: "center" }}>Merge tags:</span>
                      {MERGE_TAGS.map(t => <code key={t} className="merge">{t}</code>)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

Object.assign(window, { EmailSequences, TRIGGERS, renderTemplate });

export { defaultSeqs, renderTemplate, EmailSequences, TRIGGERS, MERGE_TAGS };
